import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import { Send, Loader2 } from 'lucide-react';
import { getGeoLocation, submitLead, getDialCodeFromCountry } from '../utils/api';

const RegistrationPopup = ({ isOpen, onClose, packageTitle, packageAmount }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [geoData, setGeoData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    contactNumber: '',
    message: ''
  });

  const countryCodes = [
    { code: '+1', country: 'US/CA' },
    { code: '+44', country: 'UK' },
    { code: '+91', country: 'India' },
    { code: '+61', country: 'Australia' },
    { code: '+86', country: 'China' },
    { code: '+81', country: 'Japan' },
    { code: '+49', country: 'Germany' },
    { code: '+33', country: 'France' },
    { code: '+39', country: 'Italy' },
    { code: '+34', country: 'Spain' },
    { code: '+52', country: 'Mexico' },
    { code: '+55', country: 'Brazil' },
    { code: '+7', country: 'Russia' },
    { code: '+27', country: 'South Africa' },
    { code: '+971', country: 'UAE' }
  ];

  // Fetch geolocation on component mount
  useEffect(() => {
    const fetchGeoLocation = async () => {
      const geoResult = await getGeoLocation();
      if (geoResult.success) {
        setGeoData(geoResult);
        // Auto-set country code based on location
        setFormData(prev => ({
          ...prev,
          countryCode: `+${geoResult.dialCode}`
        }));
      }
    };
    
    if (isOpen) {
      fetchGeoLocation();
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const leadData = {
        name: formData.name,
        email: formData.email,
        phoneNumber: formData.contactNumber,
        countryDialCode: parseInt(formData.countryCode.replace('+', '')),
        country: geoData?.countryCode || 'US',
        description: formData.message,
        ipAddress: geoData?.ipAddress || '',
        pageUrl: window.location.href,
        city: geoData?.city || '',
        state: geoData?.state || '',
        geoLocation: geoData?.fullResponse || null,
        packageTitle: packageTitle || '',
        packageAmount: packageAmount || 0
      };

      const result = await submitLead(leadData);

      if (result.success) {
        toast.success(result.message || 'Thank you! We will contact you shortly.');
        setFormData({ name: '', email: '', countryCode: '+1', contactNumber: '', message: '' });
        onClose();
        
        // Redirect to application page with customerId
        setTimeout(() => {
          navigate(`/application/main?customerid=${result.customerId}`);
        }, 1000);
      } else {
        toast.error(result.error || 'Failed to submit. Please try again.');
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Special validation for contact number - only numbers, max 10 digits
    if (name === 'contactNumber') {
      const numericValue = value.replace(/\D/g, ''); // Remove all non-digits
      if (numericValue.length <= 10) {
        setFormData({ ...formData, [name]: numericValue });
      }
      return;
    }
    
    setFormData({ ...formData, [name]: value });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-900">Start Your Registration</DialogTitle>
          <DialogDescription className="text-gray-600">
            Fill out the form below and our trademark experts will contact you to begin your registration process.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              name="name"
              type="text"
              required
              value={formData.name}
              onChange={handleChange}
              placeholder="Your full name"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleChange}
              placeholder="your@email.com"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="contactNumber">Contact Number *</Label>
            <div className="flex gap-2 mt-1">
              <select
                name="countryCode"
                value={formData.countryCode}
                onChange={handleChange}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 w-24"
              >
                {countryCodes.map((item) => (
                  <option key={item.code} value={item.code}>
                    {item.code}
                  </option>
                ))}
              </select>
              <Input
                id="contactNumber"
                name="contactNumber"
                type="tel"
                required
                value={formData.contactNumber}
                onChange={handleChange}
                placeholder="1234567890"
                maxLength={10}
                pattern="[0-9]{10}"
                title="Please enter exactly 10 digits"
                className="flex-1"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="message">What are you looking to protect?</Label>
            <Textarea
              id="message"
              name="message"
              required
              value={formData.message}
              onChange={handleChange}
              placeholder="What are you looking to protect? (e.g. Name, Logo and Slogan)"
              rows={4}
              className="mt-1"
            />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-orange-600 hover:bg-orange-700 text-white" size="lg">
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              'Get Free Consultation'
            )}
          </Button>
          <p className="text-xs text-gray-500 text-center">
            By submitting, you agree to receive calls and messages about our services.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RegistrationPopup;
